const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        
        // Handle cascade animation order
        if (elementTarget.hasAttribute('data-cascade')) {
          // For swiper slides, data-animation-order is set by SwiperBase after init
          // For other elements, set --animation-order CSS variable based on intersection index
          if (!elementTarget.classList.contains('swiper-slide')) {
            elementTarget.setAttribute('style', `--animation-order: ${index};`);
          }
        }
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

// Throttle function for performance optimization
function throttle(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// GSAP scroll trigger constants and animations
const GSAP_SCROLL_TRIGGER_CLASSNAME = 'gsap-trigger';
const GSAP_ANIMATION_OFFSCREEN_CLASSNAME = 'gsap-trigger--offscreen';

// Initialize GSAP plugins
function initializeGSAPPlugins() {
  if (typeof gsap !== 'undefined') {
    if (typeof SplitText !== 'undefined') {
      gsap.registerPlugin(SplitText);
    }
    if (typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
    }
  }
}

// GSAP scroll trigger functionality
function initializeGSAPScrollTrigger(rootEl = document, isDesignModeEvent = false) {
  if (typeof gsap === 'undefined') return;

  const gsapTriggerElements = Array.from(rootEl.getElementsByClassName(GSAP_SCROLL_TRIGGER_CLASSNAME));
  if (gsapTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    gsapTriggerElements.forEach((element) => {
      element.classList.add('gsap-trigger--design-mode');
    });
    return;
  }

  gsapTriggerElements.forEach((element) => {
    const animationType = element.dataset.gsapAnimation || 'fadeIn';
    const duration = parseFloat(element.dataset.gsapDuration) || 1;
    const delay = parseFloat(element.dataset.gsapDelay) || 0;
    const easing = element.dataset.gsapEasing || 'power2.out';

    if (animationType !== 'letterRollUp' && animationType !== 'headingFadeScale') {
      element.classList.add(GSAP_ANIMATION_OFFSCREEN_CLASSNAME);
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target;
          target.classList.remove(GSAP_ANIMATION_OFFSCREEN_CLASSNAME);
          executeGSAPAnimation(target, animationType, duration, delay, easing);
          observer.unobserve(target);
        }
      });
    }, {
      rootMargin: '0px 0px -100px 0px',
      threshold: 0.1
    });

    observer.observe(element);
  });
}

// Execute GSAP animations based on type
function executeGSAPAnimation(element, type, duration, delay, easing) {
  // Respect reduced motion preference
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    gsap.set(element, { opacity: 1, y: 0, x: 0, scale: 1, rotation: 0 });
    return;
  }

  // Add gsap-ready class for all animations
  element.classList.add('gsap-ready');

  const timeline = gsap.timeline({ delay });

  switch (type) {
    case 'fadeIn':
      gsap.fromTo(element,
        { opacity: 0 },
        { opacity: 1, duration, ease: easing }
      );
      break;

    case 'slideUp':
      gsap.fromTo(element,
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration, ease: easing }
      );
      break;

    case 'slideDown':
      gsap.fromTo(element,
        { opacity: 0, y: -50 },
        { opacity: 1, y: 0, duration, ease: easing }
      );
      break;

    case 'slideLeft':
      gsap.fromTo(element,
        { opacity: 0, x: 50 },
        { opacity: 1, x: 0, duration, ease: easing }
      );
      break;

    case 'slideRight':
      gsap.fromTo(element,
        { opacity: 0, x: -50 },
        { opacity: 1, x: 0, duration, ease: easing }
      );
      break;

    case 'scaleIn':
      gsap.fromTo(element,
        { opacity: 0, scale: 0.8 },
        { opacity: 1, scale: 1, duration, ease: easing }
      );
      break;

    case 'rotateIn':
      gsap.fromTo(element,
        { opacity: 0, rotation: -10, scale: 0.9 },
        { opacity: 1, rotation: 0, scale: 1, duration, ease: easing }
      );
      break;

    case 'letterRollUp':
      executeLetterRollUp(element, duration, delay, easing);
      break;

    case 'headingFadeScale':
      executeHeadingFadeScale(element, duration, delay, easing);
      break;

    default:
      // Default to fadeIn if animation type is not recognized
      gsap.fromTo(element,
        { opacity: 0 },
        { opacity: 1, duration, ease: easing }
      );
      break;
  }
}

// Letter Roll Up Animation - splits text into individual letters and animates them
function executeLetterRollUp(element, duration, delay, easing) {
  if (typeof gsap === 'undefined' || typeof SplitText === 'undefined') {
    element.style.opacity = '1';
    return;
  }

  element.classList.add('gsap-ready');

  const split = new SplitText(element, { type: 'words,chars' });
  const chars = split.chars;

  gsap.set(chars, {
    opacity: 0,
    y: 50,
    scale: 0.8,
    display: 'inline-block'
  });

  gsap.to(chars, {
    opacity: 1,
    y: 0,
    scale: 1,
    duration: duration || 1.2,
    ease: easing || 'power2.out',
    stagger: {
      amount: 0.3,
      from: 'start'
    },
    delay: delay || 0
  });
}

// Heading Fade Scale Animation - splits text into individual letters and animates them with fade and scale
function executeHeadingFadeScale(element, duration, delay, easing) {
  if (typeof gsap === 'undefined' || typeof SplitText === 'undefined') {
    element.style.opacity = '1';
    return;
  }

  element.classList.add('gsap-ready');

  const split = new SplitText(element, { type: 'words,chars' });
  const chars = split.chars;

  gsap.set(chars, {
    opacity: 0,
    scale: 0.8,
    y: 20,
    display: 'inline-block',
    transformOrigin: 'center center'
  });

  gsap.to(chars, {
    opacity: 1,
    scale: 1,
    y: 0,
    duration: duration || 0.8,
    ease: easing || 'back.out(1.7)',
    stagger: {
      amount: 0.4,
      from: 'start'
    },
    delay: delay || 0
  });
}

// Lenis smooth scroll initialization
function initializeLenis() {
  if (typeof Lenis !== 'undefined') {
    const lenis = new Lenis();

    // Store lenis instance globally for access by other components
    window.lenis = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Set up automatic Lenis refresh when page content changes size
    initializeLenisAutoRefresh(lenis);
  }
}

// Automatic Lenis refresh when page content changes size
function initializeLenisAutoRefresh(lenis) {
  // ResizeObserver to watch for content size changes
  if (typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver(entries => {
      // Debounce resize events to avoid excessive calls
      clearTimeout(window.lenisResizeTimeout);
      window.lenisResizeTimeout = setTimeout(() => {
        lenis.resize();
      }, 100);
    });

    // Observe the document body for size changes
    resizeObserver.observe(document.body);
  }

  // MutationObserver to catch DOM changes that might affect layout
  if (typeof MutationObserver !== 'undefined') {
    const mutationObserver = new MutationObserver(mutations => {
      let shouldRefresh = false;

      mutations.forEach(mutation => {
        // Check if nodes were added or removed
        if (mutation.type === 'childList' && (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)) {
          shouldRefresh = true;
        }
        // Check if attributes that might affect layout were changed
        else if (mutation.type === 'attributes') {
          const attrName = mutation.attributeName;
          if (attrName === 'class' || attrName === 'style' || attrName === 'hidden') {
            shouldRefresh = true;
          }
        }
      });

      if (shouldRefresh) {
        // Debounce mutation events
        clearTimeout(window.lenisMutationTimeout);
        window.lenisMutationTimeout = setTimeout(() => {
          lenis.resize();
        }, 50);
      }
    });

    // Observe DOM changes
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden']
    });
  }
}

// Navigation underline hover animation
function initializeNavigationHoverAnimation() {
  // Navigation hover is now handled via CSS ::after pseudo-element
  // No JavaScript needed for this animation
}

// Background parallax effect for featured collection sections
function initializeBackgroundParallax() {
  if (typeof gsap === 'undefined') return;

  const parallaxElements = document.querySelectorAll('.collection--has-background-image');
  
  parallaxElements.forEach((element) => {
    // Skip if reduced motion is preferred
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let elementIsVisible = false;
    
    // Create intersection observer to track visibility
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    }, {
      rootMargin: '20% 0px 20% 0px'
    });
    
    observer.observe(element);

    // Create scroll-based parallax animation
    window.addEventListener('scroll', 
      throttle(() => {
        if (!elementIsVisible) return;

        const scrollY = window.pageYOffset;
        const elementTop = element.getBoundingClientRect().top + scrollY;
        const elementHeight = element.offsetHeight;
        const windowHeight = window.innerHeight;
        
        // Calculate parallax offset
        const elementCenter = elementTop + (elementHeight / 2);
        const windowCenter = scrollY + (windowHeight / 2);
        const distance = elementCenter - windowCenter;
        
        // Apply parallax transformation to the ::before pseudo element
        // We use transform on the parent to affect the pseudo element
        const parallaxOffset = distance * 0.08; // Increased parallax movement for more impact
        
        gsap.set(element, {
          '--parallax-y': `${parallaxOffset}px`
        });
      }, 10), 
      { passive: true }
    );
  });
}

// Story Pictures parallax effect using GSAP ScrollTrigger
function initializeStoryPicturesParallax() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!window.gsap || !window.ScrollTrigger) return;

  const storyPicturesContainers = document.querySelectorAll('.story-pictures');

  storyPicturesContainers.forEach((container) => {
    const images = container.querySelectorAll('.story-pictures__image');

    images.forEach((image, index) => {
      // Different intensities based on position (z-index)
      // nth-child(1) has z-index: 3 - medium movement
      // nth-child(2) has z-index: 5 - least movement
      // nth-child(3) has z-index: 4 - most movement

      // Remap: image 0 gets multiplier 2, image 1 gets multiplier 1, image 2 gets multiplier 3
      const depthMultipliers = [2, 1, 3];
      const depthMultiplier = depthMultipliers[index];

      const yMovement = 30 * depthMultiplier; // 60px, 30px, 90px

      // Custom rotation amounts: image 1 reversed, image 3 increased
      const rotationAmounts = [16, 8, 28]; // image 1: 16deg, image 2: 8deg, image 3: 28deg
      const rotationAmount = rotationAmounts[index];

      // Custom rotation directions: reverse image 1
      const rotationDirections = [-1, -1, 1]; // image 1: reversed, image 2: -, image 3: +
      const rotationDirection = rotationDirections[index];

      // Create ScrollTrigger-based parallax animation
      // Animate from negative to positive to create parallax effect
      gsap.fromTo(image,
        {
          y: -yMovement,
          rotation: -(rotationAmount * rotationDirection)
        },
        {
          y: yMovement,
          rotation: rotationAmount * rotationDirection,
          ease: 'none',
          scrollTrigger: {
            trigger: container,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1, // Smooth scrubbing with 1 second delay
            invalidateOnRefresh: true
          }
        }
      );
    });
  });
}

// Link Button letter-by-letter hover animation
function initializeLinkButtonHoverAnimation() {
  if (typeof gsap === 'undefined' || typeof SplitText === 'undefined') return;

  const linkButtons = document.querySelectorAll('.button--link, .cooking-tab, .faq-tab');

  linkButtons.forEach((button) => {
    const buttonText = button.querySelector('.button__text, .cooking-tab__text, .faq-tab__text');
    if (!buttonText) return;

    // Skip if already initialized
    if (buttonText.splitInstance) return;

    // Create SplitText instance for this button
    const split = new SplitText(buttonText, { type: 'words,chars' });
    const chars = split.chars;

    // Set initial state
    gsap.set(chars, {
      display: 'inline-block',
      color: 'inherit',
      transformOrigin: 'center center'
    });

    // Store split instance for cleanup
    buttonText.splitInstance = split;

    // Get hover color from data attribute or use default
    const hoverColor = button.dataset.splitHoverColor || 'rgb(var(--color-link-button-hover-text))';

    const hoverInHandler = () => {
      // Kill any existing animations
      gsap.killTweensOf(chars);

      // Animate to hover color
      gsap.to(chars, {
        color: hoverColor,
        duration: 0.35,
        ease: 'power2.out',
        stagger: {
          amount: 0.12,
          from: 'start'
        }
      });
    };

    const hoverOutHandler = () => {
      // Kill any existing animations
      gsap.killTweensOf(chars);

      // Animate back to default
      gsap.to(chars, {
        color: 'inherit',
        duration: 0.25,
        ease: 'power2.out',
        stagger: {
          amount: 0.08,
          from: 'end'
        }
      });
    };

    // Add event listeners
    button.addEventListener('mouseenter', hoverInHandler);
    button.addEventListener('mouseleave', hoverOutHandler);
  });
}

window.addEventListener('DOMContentLoaded', () => {
  initializeGSAPPlugins();
  initializeLenis();
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  initializeGSAPScrollTrigger();
  initializeNavigationHoverAnimation();
  initializeLinkButtonHoverAnimation();
  initializeBackgroundParallax();
  initializeStoryPicturesParallax();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    initializeGSAPScrollTrigger(event.target, true);
    // Re-initialize navigation animation when header section is loaded
    if (event.target.classList.contains('section-header')) {
      setTimeout(() => {
        initializeNavigationHoverAnimation();
      }, 100);
    }
  });
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    initializeGSAPScrollTrigger(document, true);
    // Re-initialize navigation animation on reorder
    setTimeout(() => {
      initializeNavigationHoverAnimation();
    }, 100);
  });
}
